import {
  Landmark,
  HandshakeIcon,
  Factory,
  Globe2,
  FileText,
  MapPin,
  Shield,
  TrendingUp,
  Users2,
  ArrowRight
} from "lucide-react";

export default function PolicyAdvocacyBusinessSetup() {
  const sections = [
    {
      id: "policy-advocacy",
      title: "Policy Advocacy & Government Relations",
      icon: Landmark,
      accent:
        "Strategic interface with Central and State Governments on policy, regulation, and investment climate.",
      points: [
        "Advisory on Central and State Government industrial, sectoral, and investment policies",
        "Policy interpretation, impact assessment, and reform inputs",
        "Structured representation and engagement with government authorities",
        "Support for policy consultations and industry feedback mechanisms"
      ],
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "govt-negotiation",
      title: "Government Negotiation & Facilitation",
      icon: HandshakeIcon,
      accent:
        "End-to-end strategy and support for high-stakes engagements with government stakeholders.",
      points: [
        "Strategy and mandate preparation for government negotiations",
        "Briefing notes, position papers, and negotiation support",
        "Facilitation of engagements with ministries, departments, and state authorities",
        "Advisory on incentives, approvals, and policy concessions"
      ],
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "industry-policy",
      title: "Industry Policy & Incentive Advisory",
      icon: Factory,
      accent:
        "Aligning investments with industrial policies, incentives, and export-linked benefits.",
      points: [
        "State-specific industrial policy and sector incentive advisory",
        "Identification of subsidies, incentives, PLI, and export benefits",
        "Structuring of investment proposals aligned with government priorities",
        "Negotiation support on land, infrastructure, utilities, and fiscal benefits"
      ],
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "business-setup",
      title: "Business Setup & Market Entry Advisory (Pan-India)",
      icon: Globe2,
      accent:
        "Pan-India location strategy, entity setup, and regulatory navigation for new entrants.",
      points: [
        "Location and state selection advisory",
        "Entity structuring and regulatory approvals",
        "Single-window clearance facilitation and investor-agency coordination"
      ],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "investment-promotion",
      title: "Investment Promotion & Government Liaison",
      icon: FileText,
      accent:
        "Supporting investment proposals, MoUs, and ongoing liaison through project lifecycle.",
      points: [
        "Engagement with State Investment Promotion Boards",
        "MoU structuring with State Governments",
        "Support for investment proposals and project presentations",
        "Ongoing liaison during project setup and execution"
      ],
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "land-infra",
      title: "Land, Infrastructure & Utilities Advisory",
      icon: MapPin,
      accent:
        "Land, utilities, and connectivity solutions for greenfield and brownfield projects.",
      points: [
        "Land acquisition and industrial allotment advisory",
        "Negotiation support for power, water, logistics, and connectivity",
        "Fast-track approvals and special dispensation advisory"
      ],
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "compliance-risk",
      title: "Compliance, Risk & Policy Alignment",
      icon: Shield,
      accent:
        "Ensuring that investments and operations remain aligned with evolving regulatory landscapes.",
      points: [
        "Regulatory mapping and compliance readiness",
        "Environmental, labor, and local law advisory",
        "Policy risk assessment and dispute prevention strategies"
      ],
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "expansion-growth",
      title: "Expansion, Relocation & Growth Advisory",
      icon: TrendingUp,
      accent:
        "Supporting multi-state expansion, relocation, and growth with policy and tax efficiency.",
      points: [
        "Multi-state expansion and relocation advisory",
        "Branch, plant, and office setup across India",
        "Operational restructuring for policy and tax efficiency"
      ],
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1200&q=80&auto=format&fit=crop"
    },
    {
      id: "hni-family-office",
      title: "HNI & Family Office Advisory",
      icon: Users2,
      accent:
        "Discreet and policy-aligned advisory for strategic investments and sensitive negotiations.",
      points: [
        "Policy-aligned investment structuring",
        "Advisory for strategic and anchor investments",
        "Confidential support for sensitive government negotiations"
      ],
      image: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1200&q=80&auto=format&fit=crop"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Policy Advocacy, Government Negotiation & Business Setup Advisory
            </h1>
            <p className="text-xl md:text-2xl text-yellow-50 mb-4">
              Corporates, Businesses, HNIs & Family Offices
            </p>
            <p className="text-yellow-100 text-lg">
              Strategic interface between businesses and government to enable
              policy-aligned investments, structured negotiations, and seamless
              pan-India setup
            </p>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-black">
            Value Proposition
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            The advisory model typically serves as a strategic bridge between
            corporates, HNIs, family offices, and government institutions,
            helping clients navigate policy, secure incentives, and operationalize
            projects across Indian states while maintaining regulatory and ethical
            compliance.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-16">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={section.id}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } gap-10 items-center`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
                    <div className="absolute bottom-6 left-6 flex items-center gap-3 text-white">
                      <div className="p-3 bg-yellow-500 rounded-lg">
                        <Icon className="w-7 h-7" />
                      </div>
                      <h3 className="text-2xl font-bold">{section.title}</h3>
                    </div>
                  </div>
                </div>

                {/* Text */}
                <div className="w-full lg:w-1/2">
                  <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200 hover:border-yellow-500 transition-all duration-300">
                    <p className="text-yellow-600 font-semibold mb-4">
                      {section.accent}
                    </p>
                    <ul className="space-y-3">
                      {section.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="mt-2 inline-block w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                          <span className="text-gray-700 leading-relaxed">
                            {point}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-black">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex items-start gap-4">
            <div className="mt-1">
              <Shield className="w-7 h-7 text-yellow-500 flex-shrink-0" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                Important Disclaimer
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-2">
                All advisory and facilitation services are provided in
                compliance with applicable laws, regulations, and ethical
                standards. Final decisions and approvals rest solely with the
                respective government authorities, and no assurance is given of
                outcomes or concessions.
              </p>
              <p className="text-sm text-gray-400">
                Engagements typically focus on strategy, documentation,
                readiness, and interface support, while statutory approvals,
                licenses, and policy decisions remain the exclusive domain of
                competent public authorities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Plan a Policy-Aligned Investment or Expansion?
          </h2>
          <p className="text-lg text-yellow-50 mb-8">
            Structure mandates covering policy advocacy, negotiations, incentives,
            and pan-India business setup, tailored to corporate, business, and
            family office needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-yellow-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              Schedule a Strategy Discussion
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="mailto:info@hawkinsandbrooks.com"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-yellow-600 transition-all duration-300"
            >
              Write to the Advisory Team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
